if (!customElements.get('next-day-delivery')) {
  class NextDayDelivery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.config = this._readConfig();
      if (!this.config) return;

      this.titleEl = this.querySelector('[data-ndd-title]');
      this.detailEl = this.querySelector('[data-ndd-detail]');
      this.estimateEl = this.querySelector('[data-ndd-estimate]');
      this.estimateShortEl = this.querySelector('[data-ndd-estimate-short]');
      this.howButton = this.querySelector('[data-ndd-how]');
      this.dialogEl = this.querySelector('[data-ndd-dialog]');
      this.closeButton = this.querySelector('[data-ndd-close]');
      this.currentVariantId = this.config.variantId;
      this.manualPostal = '';
      this.location = null;
      this.timer = null;

      this._onVariantChange = this._onVariantChange.bind(this);
      this._onPostalInput = this._onPostalInput.bind(this);
      this._onHowClick = this._onHowClick.bind(this);
      this._onDialogClose = this._onDialogClose.bind(this);
      this._onDialogBackdrop = this._onDialogBackdrop.bind(this);
      document.addEventListener('product:variant-change', this._onVariantChange);
      document.addEventListener('change', this._onPostalInput);
      document.addEventListener('input', this._onPostalInput);
      if (this.howButton) this.howButton.addEventListener('click', this._onHowClick);
      if (this.closeButton) this.closeButton.addEventListener('click', this._onDialogClose);
      if (this.dialogEl) this.dialogEl.addEventListener('click', this._onDialogBackdrop);

      this._init();
    }

    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this._onVariantChange);
      document.removeEventListener('change', this._onPostalInput);
      document.removeEventListener('input', this._onPostalInput);
      if (this.howButton) this.howButton.removeEventListener('click', this._onHowClick);
      if (this.closeButton) this.closeButton.removeEventListener('click', this._onDialogClose);
      if (this.dialogEl) this.dialogEl.removeEventListener('click', this._onDialogBackdrop);
      if (this.dialogEl && this.dialogEl.open) this.dialogEl.close();
      this._stopTimer();
    }

    _readConfig() {
      const script = this.querySelector('script[type="application/json"]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    async _init() {
      this.location = await this._resolveLocation();
      this.geoEligible = this._shouldBypassGeo() || this._isGeoEligible();
      this._fillFaq();
      this._evaluate();
    }

    _shouldBypassGeo() {
      return Boolean(this.config.bypassGeo);
    }

    _onVariantChange(event) {
      const variant = event.detail?.variant;
      this.currentVariantId = variant?.id || null;
      this._evaluate();
    }

    _onPostalInput(event) {
      const field = event.target;
      if (!(field instanceof HTMLInputElement)) return;
      const name = (field.getAttribute('name') || '').toLowerCase();
      const autocomplete = (field.getAttribute('autocomplete') || '').toLowerCase();
      if (!name.includes('zip') && autocomplete !== 'postal-code') return;
      this.manualPostal = field.value || '';
      this.geoEligible = this._shouldBypassGeo() || this._isGeoEligible();
      this._evaluate();
    }

    _onHowClick() {
      if (this.dialogEl && typeof this.dialogEl.showModal === 'function') {
        this.dialogEl.showModal();
      }
    }

    _onDialogClose() {
      if (this.dialogEl && this.dialogEl.open) this.dialogEl.close();
    }

    _onDialogBackdrop(event) {
      if (event.target === this.dialogEl) this._onDialogClose();
    }

    _evaluate() {
      if (this._canShow()) {
        this._renderCopy();
        this._show();
        this._startTimer();
        return;
      }
      this._hide();
      this._stopTimer();
    }

    _canShow() {
      if (!this.config.enabled) return false;
      if (this.config.preorder) return false;
      if (!this._isVariantReady()) return false;
      if (!this.geoEligible) return false;
      return true;
    }

    _isVariantReady() {
      if (!this.currentVariantId) return false;
      const entry = (this.config.variants || []).find((variant) => String(variant.id) === String(this.currentVariantId));
      if (!entry) return false;
      if (!entry.available) return false;
      if (entry.tracked) return Number(entry.quantity) > 0;
      return true;
    }

    _isOrderWindowOpen() {
      const remaining = this._msUntilCutoff();
      if (remaining <= 0) return false;
      const now = this._zonedParts(new Date());
      return this._isBusinessDay(now.year, now.month, now.day);
    }

    _msUntilCutoff() {
      const now = new Date();
      const parts = this._zonedParts(now);
      const cutoff = this._parseCutoff();
      const cutoffMs = this._zonedWallTimeToUtc(
        parts.year,
        parts.month,
        parts.day,
        cutoff.hour,
        cutoff.minute
      );
      return cutoffMs - now.getTime();
    }

    _parseCutoff() {
      const raw = String(this.config.cutoff || '13:00').trim();
      const match = raw.match(/^(\d{1,2})(?::(\d{2}))?$/);
      if (!match) return { hour: 13, minute: 0 };
      return {
        hour: Math.min(23, Number(match[1])),
        minute: Math.min(59, Number(match[2] || 0))
      };
    }

    _timeZone() {
      return this.config.timeZone || 'America/Vancouver';
    }

    _zonedParts(date) {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: this._timeZone(),
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      });
      const parts = {};
      formatter.formatToParts(date).forEach((part) => {
        if (part.type !== 'literal') parts[part.type] = part.value;
      });
      return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour),
        minute: Number(parts.minute),
        second: Number(parts.second)
      };
    }

    _zonedOffsetMs(date) {
      const parts = this._zonedParts(date);
      const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
      return asUtc - date.getTime();
    }

    _zonedWallTimeToUtc(year, month, day, hour, minute) {
      const naiveUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
      const first = naiveUtc - this._zonedOffsetMs(new Date(naiveUtc));
      return naiveUtc - this._zonedOffsetMs(new Date(first));
    }

    _holidaySet() {
      if (this._holidays) return this._holidays;
      const values = String(this.config.holidays || '')
        .split(/[\s,;]+/)
        .map((value) => value.trim())
        .filter(Boolean);
      this._holidays = new Set(values);
      return this._holidays;
    }

    _isWeekend(year, month, day) {
      const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
      return weekday === 0 || weekday === 6;
    }

    _isBusinessDay(year, month, day) {
      if (this._isWeekend(year, month, day)) return false;
      const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      return !this._holidaySet().has(key);
    }

    _arrivalDate(offset) {
      const target = Math.max(1, Number(offset) || 1);
      const parts = this._zonedParts(new Date());
      let cursor = Date.UTC(parts.year, parts.month - 1, parts.day);
      let found = 0;
      const limit = Math.max(28, target * 4);
      for (let i = 0; i < limit; i += 1) {
        cursor += 24 * 60 * 60 * 1000;
        const date = new Date(cursor);
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth() + 1;
        const day = date.getUTCDate();
        if (this._isBusinessDay(year, month, day)) {
          found += 1;
          if (found === target) return { year, month, day };
        }
      }
      return null;
    }

    _calendarDaysUntil(dateParts) {
      if (!dateParts) return 1;
      const parts = this._zonedParts(new Date());
      const start = Date.UTC(parts.year, parts.month - 1, parts.day);
      const end = Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day);
      return Math.max(1, Math.round((end - start) / 86400000));
    }

    _deliverySchedule() {
      const businessDays = this._deliveryDayCount();
      const arrival = this._arrivalDate(businessDays);
      return {
        arrival,
        days: this._calendarDaysUntil(arrival)
      };
    }

    _formatDate(dateParts) {
      if (!dateParts) return '';
      const date = new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day, 12, 0, 0));
      return new Intl.DateTimeFormat(this.config.locale || 'en-CA', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      }).format(date);
    }

    _formatCutoff() {
      const cutoff = this._parseCutoff();
      const date = new Date(Date.UTC(2020, 0, 1, cutoff.hour, cutoff.minute, 0));
      const locale = this.config.locale === 'fr' ? 'fr-CA' : 'en-US';
      return new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: locale !== 'fr-CA',
        timeZone: 'UTC'
      }).format(date);
    }

    _formatCountdown(ms) {
      const totalMinutes = Math.max(0, Math.floor(ms / 60000));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      if (hours > 0) return `${hours}h ${minutes}min`;
      return `${minutes}min`;
    }

    _escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    _replaceToken(value, token, replacement) {
      return String(value).split(token).join(replacement);
    }

    _cityName() {
      const city = String(this.location?.city || this.config.customerCity || '').trim();
      if (city) return city;
      return String(this.config.copy?.cityFallback || '').trim();
    }

    _fillFaq() {
      this._applyHowTokens();

      const badges = this.querySelector('[data-ndd-badges]');
      if (!badges || badges.childElementCount) return;
      this._regionList().forEach((name) => {
        const badge = document.createElement('span');
        badge.className = 'next-day-delivery__badge';
        badge.textContent = name;
        badges.appendChild(badge);
      });
    }

    _applyHowTokens() {
      const body = this.querySelector('[data-ndd-how-body]');
      if (!body) return;
      if (this._howBodyTemplate == null) this._howBodyTemplate = body.innerHTML;

      const businessDays = this._deliveryDayCount();
      const arrival = this._arrivalDate(businessDays);
      const days = this._calendarDaysUntil(arrival);
      let html = this._howBodyTemplate;
      html = this._replaceToken(html, '[cutoff]', this._escapeHtml(this._formatCutoff()));
      html = this._replaceToken(html, '[day-written]', this._escapeHtml(this._dayWritten(days)));
      html = this._replaceToken(html, '[day]', this._escapeHtml(String(days)));
      html = this._replaceToken(html, '[date]', this._escapeHtml(this._formatDate(arrival)));
      html = this._replaceToken(html, '[regions]', this._escapeHtml(this.config.regions || ''));
      body.innerHTML = html;
    }

    _regionList() {
      return String(this.config.regions || '')
        .split(/[,;]+/)
        .map((value) => value.replace(/\.+$/, '').trim())
        .filter(Boolean);
    }

    _dayWritten(days) {
      const value = Math.max(0, Number(days) || 0);
      const locale = String(this.config.locale || 'en').toLowerCase();
      const words = locale.startsWith('fr') ? this._frenchNumber(value) : this._englishNumber(value);
      return words || String(value);
    }

    _englishNumber(value) {
      const ones = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
      const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
      if (value < 20) return ones[value];
      if (value < 100) {
        const ten = tens[Math.floor(value / 10)];
        const one = value % 10;
        return one ? `${ten}-${ones[one]}` : ten;
      }
      return '';
    }

    _frenchNumber(value) {
      if (value >= 100) return '';
      const ones = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize'];
      if (value < 17) return ones[value];
      if (value < 20) return `dix-${ones[value - 10]}`;
      if (value < 70) {
        const names = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante'];
        const ten = names[Math.floor(value / 10)];
        const one = value % 10;
        if (!one) return ten;
        if (one === 1) return `${ten} et un`;
        return `${ten}-${ones[one]}`;
      }
      if (value === 71) return 'soixante et onze';
      if (value < 80) return `soixante-${this._frenchNumber(value - 60)}`;
      if (value === 80) return 'quatre-vingts';
      return `quatre-vingt-${this._frenchNumber(value - 80)}`;
    }

    _copyTokenHtml() {
      const schedule = this._deliverySchedule();
      const days = schedule.days;
      const countdownText = this._isOrderWindowOpen()
        ? this._formatCountdown(this._msUntilCutoff())
        : '';
      const countdownHtml = countdownText
        ? `<span class="next-day-delivery__countdown">${this._escapeHtml(countdownText)}</span>`
        : '';
      return {
        '[countdown]': countdownHtml,
        '[cutoff]': `<span data-ndd-cutoff>${this._escapeHtml(this._formatCutoff())}</span>`,
        '[date]': `<span class="next-day-delivery__date" data-ndd-date>${this._escapeHtml(this._formatDate(schedule.arrival))}</span>`,
        '[day-written]': `<span class="next-day-delivery__day" data-ndd-day-written>${this._escapeHtml(this._dayWritten(days))}</span>`,
        '[day]': `<span class="next-day-delivery__day" data-ndd-day>${this._escapeHtml(String(days))}</span>`,
        '[city]': `<span class="next-day-delivery__city" data-ndd-city>${this._escapeHtml(this._cityName())}</span>`
      };
    }

    _unwrapRichText(value) {
      const html = String(value || '').trim();
      const match = html.match(/^<p>([\s\S]*)<\/p>$/i);
      if (!match || /<p[\s>]/i.test(match[1])) return html;
      return match[1];
    }

    _applyCopyTokens(template, tokens) {
      let html = this._unwrapRichText(template);
      const values = tokens || this._copyTokenHtml();
      Object.keys(values).forEach((token) => {
        html = this._replaceToken(html, token, values[token]);
      });
      return html;
    }

    _paintFields() {
      const copy = this.config.copy || {};
      const tokens = this._copyTokenHtml();
      if (this.titleEl) this.titleEl.innerHTML = this._applyCopyTokens(copy.title, tokens);
      if (this.detailEl) {
        const detail = this._isOrderWindowOpen() ? copy.detail : copy.detailAfterCutoff;
        const html = this._applyCopyTokens(detail, tokens);
        const visible = this._unwrapRichText(detail).replace(/<[^>]*>/g, '').replace(/&nbsp;|\u00a0/g, ' ').trim();
        this.detailEl.hidden = !visible;
        this.detailEl.innerHTML = visible ? html : '';
      }
      if (this.estimateEl) this.estimateEl.innerHTML = this._applyCopyTokens(copy.estimate, tokens);
      if (this.estimateShortEl) this.estimateShortEl.innerHTML = this._applyCopyTokens(copy.estimateShort, tokens);
      this._paintCountdown(tokens);
    }

    _renderCopy() {
      this._orderWindowOpen = this._isOrderWindowOpen();
      this._applyHowTokens();
      this._paintFields();
    }

    _countdownTemplate() {
      const html = this._unwrapRichText(this.config.copy && this.config.copy.countdown);
      const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;|\u00a0/g, ' ').trim();
      return text ? html : '';
    }

    _paintCountdown(tokens) {
      const template = this._countdownTemplate();
      const lines = this.querySelectorAll('.next-day-delivery__remaining, .next-day-delivery__remaining-mobile');
      if (!template || !this._isOrderWindowOpen()) {
        lines.forEach((el) => {
          el.hidden = true;
        });
        return;
      }
      lines.forEach((el) => {
        el.hidden = false;
      });
      const html = this._applyCopyTokens(template, tokens);
      this.querySelectorAll('[data-ndd-countdown-line]').forEach((el) => {
        el.innerHTML = html;
      });
    }

    _updateCountdown() {
      if (!this._canShow()) {
        this._hide();
        this._stopTimer();
        return;
      }
      const open = this._isOrderWindowOpen();
      if (open !== this._orderWindowOpen) {
        this._renderCopy();
        return;
      }
      this._paintFields();
    }

    _startTimer() {
      if (this.timer) return;
      this.timer = window.setInterval(() => this._updateCountdown(), 1000);
    }

    _stopTimer() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    _show() {
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
    }

    _hide() {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      if (this.dialogEl && this.dialogEl.open) this.dialogEl.close();
    }

    _parsePostalList() {
      if (this._postalList) return this._postalList;
      const list = [];
      String(this.config.postalCodes || '')
        .split(/[,;\n\r]+/)
        .forEach((chunk) => {
          const trimmed = chunk.trim();
          if (!trimmed) return;
          if (trimmed.includes('/')) {
            const entry = this._parsePostalEntry(trimmed);
            if (entry) list.push(entry);
            return;
          }
          trimmed.split(/\s+/).forEach((part) => {
            const entry = this._parsePostalEntry(part);
            if (entry) list.push(entry);
          });
        });
      this._postalList = list;
      return this._postalList;
    }

    _parsePostalEntry(entry) {
      const trimmed = String(entry || '').trim();
      if (!trimmed) return null;
      const match = trimmed.match(/^(.+?)\s*\/\s*#?\s*(\d+)\s*$/);
      const code = this._normalizePostal(match ? match[1] : trimmed);
      if (!code) return null;
      const parsedDays = match ? Number(match[2]) : NaN;
      const days = Number.isFinite(parsedDays) && parsedDays > 0 ? parsedDays : 1;
      return { code, days };
    }

    _normalizePostal(value) {
      return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    }

    _postalEntry(postal) {
      const normalized = this._normalizePostal(postal);
      if (!normalized) return null;
      const fsa = normalized.slice(0, 3);
      let best = null;
      this._parsePostalList().forEach((entry) => {
        const allowed = entry.code;
        const matches = allowed.length <= 3
          ? fsa === allowed
          : normalized === allowed || normalized.startsWith(allowed);
        if (!matches) return;
        if (!best || allowed.length > best.code.length) best = entry;
      });
      return best;
    }

    _postalMatches(postal) {
      return Boolean(this._postalEntry(postal));
    }

    _deliveryDayCount() {
      let days = 1;
      const manual = String(this.manualPostal || '').trim();
      if (manual) {
        const manualEntry = this._postalEntry(manual);
        if (manualEntry) days = manualEntry.days;
      } else {
        const sources = [this.config.customerPostal, this.location && this.location.postal];
        for (let i = 0; i < sources.length; i += 1) {
          const entry = this._postalEntry(sources[i]);
          if (entry) {
            days = entry.days;
            break;
          }
        }
      }

      if (!this._isOrderWindowOpen()) days += 1;
      return days;
    }

    _haversineKm(lat1, lon1, lat2, lon2) {
      const toRad = (value) => (value * Math.PI) / 180;
      const earthKm = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
      return earthKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    _isGeoEligible() {
      const postals = [this.manualPostal, this.config.customerPostal, this.location?.postal];
      const postalMatch = postals.some((postal) => this._postalMatches(postal));

      const loc = this.location;
      const lat = Number(this.config.originLat);
      const lon = Number(this.config.originLng);
      const radius = Number(this.config.radiusKm);
      const hasOrigin = Number.isFinite(lat) && Number.isFinite(lon) && Number.isFinite(radius) && radius > 0;

      if (loc && hasOrigin && Number.isFinite(loc.lat) && Number.isFinite(loc.lon)) {
        // if (loc.country && loc.country !== 'CA') {
        //   return postalMatch;
        // }
        const distance = this._haversineKm(loc.lat, loc.lon, lat, lon);
        if (distance <= radius) return true;
      }

      return postalMatch;
    }

    async _resolveLocation() {
      const cached = this._readGeoCache();
      if (cached) return cached;

      const fromRebuy = await this._fetchRebuyGeo();
      if (fromRebuy) {
        this._writeGeoCache(fromRebuy);
        return fromRebuy;
      }

      return null;
    }

    async _fetchRebuyGeo() {
      try {
        const response = await fetch('https://geo.rebuyengine.com/?key=e9df80ad503aa371f73c30bed2d854fb099f876d');
        if (!response.ok) return null;
        const payload = await response.json();
        const data = payload && payload.data;
        if (!data || !data.latitude || !data.longitude || !data.postal_code) return null;
        return {
          lat: Number(data.latitude),
          lon: Number(data.longitude),
          postal: data.postal_code || '',
          country: String(data.country_code || '').toUpperCase(),
          city: data.city_name || ''
        };
      } catch (error) {
        return null;
      }
    }

    _readGeoCache() {
      try {
        const raw = sessionStorage.getItem('roveNextDayDeliveryGeoV2');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || Date.now() - parsed.ts > 30 * 60 * 1000) return null;
        return parsed;
      } catch (error) {
        return null;
      }
    }

    _writeGeoCache(location) {
      try {
        sessionStorage.setItem('roveNextDayDeliveryGeoV2', JSON.stringify({ ...location, ts: Date.now() }));
      } catch (error) {
        return;
      }
    }
  }

  customElements.define('next-day-delivery', NextDayDelivery);
}
